import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_API_URL || '';
const INACTIVITY_TIMEOUT = 20 * 60 * 1000; // 20 minutes
const WARNING_BEFORE = 2 * 60 * 1000; // warn 2 min before

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);
  const warningRef = useRef(null);

  const logout = useCallback(() => {
    sessionStorage.removeItem('hd_token');
    sessionStorage.removeItem('hd_user');
    setUser(null);
    clearTimeout(timerRef.current);
    clearTimeout(warningRef.current);
    window.location.href = '/login';
  }, []);

  const resetTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    clearTimeout(warningRef.current);
    warningRef.current = setTimeout(() => {
      if (window.confirm('⚠️ You will be logged out in 2 minutes due to inactivity. Click OK to stay logged in.')) {
        resetTimer();
      }
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE);
    timerRef.current = setTimeout(logout, INACTIVITY_TIMEOUT);
  }, [logout]);

  useEffect(() => {
    const token = sessionStorage.getItem('hd_token');
    const savedUser = sessionStorage.getItem('hd_user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      resetTimer();
    }
    setLoading(false);
    const events = ['mousedown','keydown','scroll','touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    return () => { events.forEach(e => window.removeEventListener(e, resetTimer)); clearTimeout(timerRef.current); clearTimeout(warningRef.current); };
  }, [resetTimer]);

  const login = async (email, password) => {
    const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { token, user: u } = res.data;
    sessionStorage.setItem('hd_token', token);
    sessionStorage.setItem('hd_user', JSON.stringify(u));
    setUser(u);
    resetTimer();
    return u;
  };

  const can = (...roles) => user && roles.includes(user.role);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
